export type TradeWindowTf = '1m' | '5m' | '15m' | '30m' | '60m' | '1d';

const TF_SEC: Record<TradeWindowTf, number> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '60m': 3600,
  '1d': 86400,
};

const WINDOW_BUCKET_SEC: Record<TradeWindowTf, number> = {
  '1m': 300,
  '5m': 1800,
  '15m': 7200,
  '30m': 14400,
  '60m': 86400,
  '1d': 604800,
};

// Window sizing per timeframe.
// - BOT: wall-clock bars of context before the trade open bucket.
// - ATC: bars of post-trade context after the trade close bucket.
// - MAX_TOTAL: hard gate on `totalBars = BOT + tradeBars + ATC`. `tradeBars` is
//   measured in WALL-CLOCK bars between open and close (nights/weekends count),
//   so this gate must stay high enough to keep the required multi-day Full
//   Session contexts ENABLED — otherwise the UI silently falls back to a coarser
//   timeframe and drops the requested window entirely:
//     1m  Full 1 day :  600 +   960 + 120 = 1680
//     5m  Full 3 days:  432 +   864 +  80 = 1376   (3-day wall-clock span)
//     15m Full 10 days: 960 +   960 +  60 = 1980   (10-day wall-clock span)
//     30m Full 20 days: 960 +   960 +  40 = 1960   (20-day wall-clock span)
//     60m Full 40 days: 960 +   960 +  40 = 1960   (40-day wall-clock span)
// 2200 keeps every required context enabled while still disabling pathologically
// long trades (a deliberate product behavior that falls back to a coarser tf).
const WINDOW_CFG: Record<TradeWindowTf, { BOT: number; ATC: number; MAX_TOTAL: number }> = {
  '1m': { BOT: 600, ATC: 120, MAX_TOTAL: 2000 },
  '5m': { BOT: 432, ATC: 80, MAX_TOTAL: 1500 },
  '15m': { BOT: 960, ATC: 60, MAX_TOTAL: 2200 },
  '30m': { BOT: 960, ATC: 40, MAX_TOTAL: 2200 },
  '60m': { BOT: 960, ATC: 40, MAX_TOTAL: 2200 },
  '1d': { BOT: 250, ATC: 20, MAX_TOTAL: 500 },
};

const TF_ALIASES: Record<string, TradeWindowTf> = {
  '1m': '1m',
  '1min': '1m',
  '5m': '5m',
  '5min': '5m',
  '15m': '15m',
  '15min': '15m',
  '30m': '30m',
  '30min': '30m',
  '60m': '60m',
  '60min': '60m',
  '1h': '60m',
  '1d': '1d',
  '1day': '1d',
};

export function toTradeWindowTf(timeframe: string): TradeWindowTf | null {
  const normalized = String(timeframe || '').trim().toLowerCase();
  return TF_ALIASES[normalized] ?? null;
}

export function timeframeToSec(timeframe: string): number {
  const tf = toTradeWindowTf(timeframe);
  if (!tf) return 0;
  return TF_SEC[tf];
}

export type TradeWindowResult = {
  tf: TradeWindowTf;
  tfSec: number;
  openBucketSec: number;
  closeBucketSec: number;
  rawStartSec: number;
  rawEndSec: number;
  bucketedStartSec: number;
  bucketedEndSec: number;
  tradeBars: number;
  totalBars: number;
  enabled: boolean;
  BOT: number;
  ATC: number;
  MAX_TOTAL: number;
};

export function computeTradeWindow(input: {
  openMs: number;
  closeMs?: number;
  timeframe: string;
}): TradeWindowResult | null {
  const tf = toTradeWindowTf(input.timeframe);
  if (!tf) return null;

  const openMs = Number(input.openMs);
  if (!Number.isFinite(openMs) || openMs <= 0) return null;

  const nowMs = Date.now();
  const closeMsInput = Number(input.closeMs);
  const closeMsRaw = Number.isFinite(closeMsInput) && closeMsInput > 0 ? closeMsInput : nowMs;
  const closeMs = Math.max(openMs, closeMsRaw);

  const tfSec = TF_SEC[tf];
  const cfg = WINDOW_CFG[tf];

  const openSec = Math.floor(openMs / 1000);
  const closeSec = Math.floor(closeMs / 1000);

  const openBucketSec = Math.floor(openSec / tfSec) * tfSec;
  const closeBucketSec = Math.floor(closeSec / tfSec) * tfSec;

  const tradeBars = Math.max(1, Math.ceil((closeBucketSec - openBucketSec) / tfSec) + 1);
  const totalBars = cfg.BOT + tradeBars + cfg.ATC;
  const enabled = totalBars <= cfg.MAX_TOTAL;

  const rawStartSec = openBucketSec - cfg.BOT * tfSec;
  const rawEndSec = closeBucketSec + cfg.ATC * tfSec;

  const bucketSec = WINDOW_BUCKET_SEC[tf];
  const bucketedStartSec = Math.floor(rawStartSec / bucketSec) * bucketSec;
  let bucketedEndSec = Math.ceil(rawEndSec / bucketSec) * bucketSec;
  if (bucketedEndSec <= bucketedStartSec) bucketedEndSec = bucketedStartSec + bucketSec;

  return {
    tf,
    tfSec,
    openBucketSec,
    closeBucketSec,
    rawStartSec,
    rawEndSec,
    bucketedStartSec,
    bucketedEndSec,
    tradeBars,
    totalBars,
    enabled,
    BOT: cfg.BOT,
    ATC: cfg.ATC,
    MAX_TOTAL: cfg.MAX_TOTAL,
  };
}

/**
 * Expands a trade context window so the requested data covers the complete
 * detected session of the trade date. The BOT/ATC context window is centered on
 * the trade (open - BOT .. close + ATC), which for a morning trade ends long
 * before the afternoon session — so the server clips the afternoon bars away
 * and the chart's session viewport has nothing to show after ~noon.
 *
 * The session range is supplied by the caller (from automatic session
 * detection) as an [startSec, endSec) epoch-second window for the trade date.
 *
 * The `enabled` gate is intentionally left untouched — the UI timeframe
 * eligibility decision stays based on the original trade context window.
 */
export function expandTradeWindowToSession(
  window: TradeWindowResult | null,
  sessionRangeSec: { startSec: number; endSec: number },
): TradeWindowResult | null {
  if (!window || !window.enabled) return window;

  return {
    ...window,
    // Never shrink: BOT context already reaches before the session open.
    bucketedStartSec: Math.min(window.bucketedStartSec, sessionRangeSec.startSec),
    // Extend past the close+ATC end so the whole session is fetchable.
    bucketedEndSec: Math.max(window.bucketedEndSec, sessionRangeSec.endSec),
  };
}
